import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFundingCreatePrefetch, apiFundingStore } from "../lib/api";

export default function FundingCreate() {
  const [search] = useSearchParams();
  const forcedCurrency = (search.get("currency") || "EUR").toUpperCase();

  const [loading, setLoading] = useState(true);
  const [prefetch, setPrefetch] = useState<any>(null);
  const [method, setMethod] = useState<"SEPA" | "SWIFT">("SEPA");
  const [amount, setAmount] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const res = await apiFundingCreatePrefetch();
      setPrefetch(res);
      setLoading(false);
    })();
  }, []);

  const selected = useMemo(() => {
    const accs = (prefetch?.accounts ?? []) as any[];
    const eur = accs.find((a) => a.status === "approved" && a.currency === "EUR");
    return eur ?? accs.find((a) => a.status === "approved") ?? null;
  }, [prefetch]);

  const trustName =
    selected?.user?.trustEntity?.trust_name ??
    (selected?.label || `#${selected?.id ?? ""}`);

  // Prefer server-computed (Blade-parity) value; fallback to local reconstruction
  const sender = useMemo(() => {
    const fromServer = (prefetch?.expected_sender_name ?? "").trim();

    if (fromServer) return fromServer;

    const biz = (prefetch?.user?.businessProfile?.company_name ?? "").trim();
    if (biz) return biz;

    const first = (prefetch?.user?.userData?.first_name ?? "").trim();
    const last = (prefetch?.user?.userData?.last_name ?? "").trim();
    const local = `${first} ${last}`.trim();

    return local || "—";
  }, [prefetch]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!selected) return;

    const n = Number(amount);
    if (!Number.isFinite(n) || n < 1000) {
      setError("Amount must be at least 1,000.");
      return;
    }

    try {
      const res = await apiFundingStore({
        account_id: selected.id,
        currency: forcedCurrency,
        method,
        amount: n,
      });

      if (res.requiresDocs && res.funding?.id) {
        navigate(`/compliance/funding/${res.funding.id}/upload`);
        return;
      }

      navigate(`/funding/${res.funding.id}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unexpected error.");
    }
  }

  if (loading) {
    return <div className="section-box skeleton">Loading…</div>;
  }

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-28 md:pb-10"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 5.5rem)" }}
    >
      {/* Form */}
      <div className="lg:col-span-2 card-box">
        {!selected && (
          <div className="alert-info mb-4">
            <div className="font-semibold mb-1">No approved account</div>
            <p className="text-sm">
              You don’t have an approved account available for funding yet. Please create or wait
              for approval, then return here to generate funding instructions.
            </p>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium">Trust</label>
            <div className="input bg-gray-50">{trustName || "—"}</div>
          </div>

          <div>
            <label className="block text-sm font-medium">Currency</label>
            <input className="input bg-gray-50" value={forcedCurrency} disabled />
          </div>

          <div>
            <label className="block text-sm font-medium">Method</label>
            <select
              className="input w-full"
              value={method}
              onChange={(e) => setMethod(e.target.value as any)}
            >
              <option value="SEPA">SEPA</option>
              <option value="SWIFT">SWIFT</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Amount</label>
            <input
              type="number"
              step="0.01"
              min={1000}
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="input w-full"
              placeholder="Minimum €1,000"
            />
            <p className="text-xs text-gray-500 mt-1">
              Amounts ≥ €10,000 will require compliance documents unless overridden.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium">Sender (must match your profile)</label>
            <div className="input bg-gray-50 font-semibold">{sender || "—"}</div>
          </div>

          {error && <div className="alert-danger">{error}</div>}

          {/* Submit row in normal flow (visible on all breakpoints) */}
          <div className="flex items-center gap-3 pt-2 mb-2">
            <button className="btn" disabled={!selected} aria-disabled={!selected}>
              Generate Reference & Instructions
            </button>
            <Link to="/funding" className="btn btn-white">
              Cancel
            </Link>
          </div>
        </form>
      </div>

      {/* Summary */}
      <div className="card-box">
        <h3 className="text-lg font-semibold mb-4">
          {prefetch?.totals?.title || "Trust Funding"}
        </h3>
        <DL
          label="Total Received"
          value={fmt(prefetch?.totals?.total_received)}
          currency={prefetch?.totals?.currency || "EUR"}
        />
        <DL
          label="Pending Execution"
          value={fmt(prefetch?.totals?.pending_execution)}
          currency={prefetch?.totals?.currency || "EUR"}
        />
        <DL
          label="Queued for Trust Funding"
          value={fmt(prefetch?.totals?.queued_for_funding)}
          currency={prefetch?.totals?.currency || "EUR"}
        />
      </div>
    </div>
  );
}

function DL({ label, value, currency }: { label: string; value: string; currency: string }) {
  return (
    <div className="flex justify-between mb-2">
      <dt className="text-sm text-gray-600">{label}:</dt>
      <dd className="font-semibold">
        {value} {currency}
      </dd>
    </div>
  );
}

function fmt(n: any) {
  const v = Number(n ?? 0);
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
